import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  InputAdornment,
  IconButton,
  Card,
  CardContent
} from "@mui/material";
import { Assessment, Title, Event, AccessTime, CalendarToday, EmailOutlined } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Save from "@mui/icons-material/Save";
import { headers, tenant } from "../../utils/utils";
import SuggestInput from "../../Screen/Ticket/SuggestInput";

const ReportsConfig = ({ memberList, tagsList, ticketStatus, displayAlertMessage, reportLoaded, setReportLoaded }) => {
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportConfigList, setReportConfigList] = useState([]);
  const [position, setPosistion] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // NEW: local copy of form data
  const [localReport, setLocalReport] = useState(reportLoaded);

  useEffect(() => {
    // Report opened
    localStorage.setItem("REPORT_OPEN", "true");
  
    return () => {
      // Report closed (tab switch, navigation, refresh)
      localStorage.setItem("REPORT_OPEN", "false");
    };
  }, []);
  
  // Sync local state if parent reportLoaded changes (edit mode or new report)
  useEffect(() => {
   
    if (reportLoaded && reportLoaded.id) {
      setEditMode(true);
      setLocalReport(reportLoaded);
    }
    
  }, [reportLoaded]);

  
  useEffect(() => {
    reportList();
  }, []);

  const handleLocalChange = useCallback((e) => {
    const { name, value } = e.target;
    setLocalReport((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleWeeklyDayChange = (day) => {
    setLocalReport((prev) => {
      const currentDays = Array.isArray(prev.day) ? prev.day : [];
      return {
        ...prev,
        day: currentDays.includes(day)
          ? currentDays.filter((d) => d !== day)
          : [...currentDays, day],
      };
    });
  };

  const handleGenerateReport = async () => {
    const payload = {
      ...localReport,
      tenant: tenant(),
      board: localStorage.getItem("board"),
      query: responseData?.query || "",
      queryHeader: responseData?.queryHeader || "",
    };

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/ticket/scheduleReport`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      displayAlertMessage("Report schedule saved successfully.");
      reportList();
    } catch (error) {
      console.error("Error saving report:", error);
      setResponseData("Failed to save report.");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    try {
      setLoading(true);
      // Sync to parent before request
      const response = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/ticket/chat`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({
            story: localReport.story,
            board: localStorage.getItem("board"),
          }),
        }
      );

      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      setResponseData(data);
    } catch (error) {
      console.error("Error sending message:", error);
      setResponseData("Sorry, I can't understand. Can you change your story?");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      console.log(localReport.id)
      setReportLoaded(localReport); // Sync with parent before saving
      const response = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/ticket/reportUpdated/${localReport.id}`,
        {
          method: "PUT",
          headers: {
            ...headers(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify(localReport),
        }
      );

      if (response.ok) {
        displayAlertMessage("Report schedule updated successfully.");
        reportList();
      } else {
        console.error("Failed to update:", response.statusText);
      }
    } catch (error) {
      console.error("Error updating report:", error);
    }

    setEditMode(false);
    setLocalReport({
      frequency: "",
      email: "",
      reportName: "",
      time: "",
      day: [],
      date: "",
      story: "",
    });
  };

  const handleEdit = (rowIndex) => {
    setPosistion(rowIndex);
    const report = reportConfigList[rowIndex];
    setLocalReport({
      frequency: report.frequency || "",
      email: report.email || "",
      story: report.story || "",
      reportName: report.reportName || "",
      time: report.time || "",
      query: report.query || "",
      queryHeader:report.queryHeader || "",
      day: Array.isArray(report.day) ? report.day : [],
      date: report.date || "",
      board:report.board || "",
      tenant:report.tenant || "",
      id:rowIndex
    });
    setEditMode(true);
  };

  const reportList = async () => {
    const report = await fetch(
      `${process.env.REACT_APP_TICKET_SERVER}/ticket/reportList`,
      {
        method: "GET",
        headers: headers(),
      }
    );
    const data = await report.json();
    setReportConfigList(
      data.filter(
        (item) =>
          item.tenant === tenant() &&
          item.board === localStorage.getItem("board")
      )
    );
  };

  const handleDelete = async (indexId) => {
    const response = await fetch(
      `${process.env.REACT_APP_TICKET_SERVER}/ticket/deleteReport/${indexId}`,
      {
        method: "POST",
        headers: headers(),
      }
    );
    if (response.ok) displayAlertMessage("Report schedule deleted successfully.");
    reportList();
  };

  const renderResponse = () => {
    if (
      responseData &&
      typeof responseData === "object" &&
      responseData.queryHeader &&
      responseData.queryData
    ) {
      const headersArray = responseData.queryHeader
        .split(",")
        .map((header) => header.trim());
      return (
        <Card sx={{ margin: "auto", padding: 2, mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Preview</Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {headersArray.map((header, index) => (
                      <TableCell key={index}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {responseData.queryData.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {headersArray.map((header, colIndex) => (
                        <TableCell key={colIndex}>
                          {row[header.toLowerCase().replace(/ /g, "_")] || ""}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Assessment />}
              sx={{ mt: 2 }}
              onClick={handleGenerateReport}
            >
              Generate Report
            </Button>
          </CardContent>
        </Card>
      );
    } else if (typeof responseData === "string") {
      return <Typography variant="body1" sx={{ mt: 2 }}>{responseData}</Typography>;
    }
    return null;
  };

  return (
    <>
      {/* Report List Table */}
      <TableContainer component={Paper} sx={{ mt: 2, mb: 4, maxHeight: "400px" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {["Report Name", "Frequency", "Story", "Email", "Time", "Day", "Date", "Actions"].map((header) => (
                <TableCell key={header} sx={{ fontWeight: "bold" }}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {reportConfigList.map((item, rowIndex) => (
              <TableRow key={rowIndex}>
                <TableCell>{item.reportName}</TableCell>
                <TableCell>{item.frequency}</TableCell>
                <TableCell>{item.story}</TableCell>
                <TableCell>
                  {item.email.split(",").map((email, index) => (
                    <div key={index}>{email.trim()}</div>
                  ))}
                </TableCell>
                <TableCell>{item.time}</TableCell>
                <TableCell>{item.day}</TableCell>
                <TableCell>{item.date}</TableCell>
                <IconButton color="primary" onClick={() => handleEdit(rowIndex)}><EditIcon /></IconButton>
                <IconButton color="error" onClick={() => handleDelete(rowIndex)}><DeleteIcon /></IconButton>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Form */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={3} sx={{ p: 3, mt: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    variant="outlined"
                    name="reportName"
                    value={localReport.reportName}
                    onChange={handleLocalChange}
                    // onMouseOut={() => setReportLoaded(localReport)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Title /></InputAdornment> }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="Report Frequency"
                    name="frequency"
                    value={localReport.frequency}
                    onChange={handleLocalChange}
                    // onMouseOut={() => setReportLoaded(localReport)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Event /></InputAdornment> }}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </TextField>
                </Grid>

                {localReport.frequency === "weekly" && (
                  <Grid item xs={12}>
                    <FormGroup row>
                      {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => (
                        <FormControlLabel
                          key={day}
                          control={<Checkbox checked={localReport.day.includes(day)} onChange={() => handleWeeklyDayChange(day)} />}
                          label={day}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                )}

                {localReport.frequency === "monthly" && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Day of the Month"
                      type="number"
                      name="date"
                      value={localReport.date}
                      onChange={handleLocalChange}
                      // onMouseOut={() => setReportLoaded(localReport)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday /></InputAdornment> }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Report Time"
                    type="time"
                    name="time"
                    value={localReport.time}
                    // onMouseOut={() => setReportLoaded(localReport)}
                    onChange={handleLocalChange}
                    InputProps={{ startAdornment: <InputAdornment position="start"><AccessTime /></InputAdornment> }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={localReport.email}
                    onChange={handleLocalChange}
                    helperText="Use commas to separate multiple emails"
                    // onMouseOut={() => setReportLoaded(localReport)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlined /></InputAdornment> }}
                  />
                </Grid>

                {!editMode && (
                  <Grid item xs={12}>
                    <SuggestInput
                      memberList={memberList}
                      tagsList={tagsList}
                      ticketStatus={ticketStatus}
                      setLocalReport={setLocalReport}
                      localReport={localReport}
                      setReportLoaded={setReportLoaded}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                  {editMode ? (
                    <Tooltip title="Save Changes">
                      <Button variant="contained" color="primary" onClick={handleSave} startIcon={<Save />}>
                        {loading ? "Saving..." : "Update"}
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Proceed to Next Step">
                      <Button variant="contained" color="success" onClick={handleNext}>
                        {loading ? "Loading..." : "Next"}
                      </Button>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {renderResponse()}
        </Grid>
      </Grid>
    </>
  );
};

export default ReportsConfig;
